/* 
  QA validation utilities for LE Transcriber
  - Parse transcript and metadata
  - Detect violations against labeling guidelines
  - Offer safe auto-fixes (spacing around/inside tags, canonical tag names)
*/

export type QAIssueSeverity = 'error' | 'warning' | 'info'

export interface QAIssue {
  id: string
  severity: QAIssueSeverity
  code: string
  message: string
  index: number // start index in text for navigation
  length: number // length of problematic span
  sample?: string // snippet preview
}

export interface MetaLike {
  language: string
  meeting: boolean
  speakers: { name: string; gender?: string; dialect?: string }[]
  notes?: string
}

export interface QAResult {
  issues: QAIssue[]
  counts: { error: number; warning: number; info: number; total: number }
}

const TIMESTAMP = '\\d\\d:\\d\\d:\\d\\d\\.\\d{3}'
const TIMESTAMP_RE = new RegExp(`^${TIMESTAMP}$`)
const RANGE_RE = new RegExp(`^(${TIMESTAMP})-(${TIMESTAMP})$`)
const ANY_RANGE_RE = new RegExp(`${TIMESTAMP}-${TIMESTAMP}`, 'g')

const CANONICAL_TAGS = {
  singleNoTs: ['laughter', 'clapping', 'music', 'noise', 'singing', 'partial sentence'] as const,
  requiresRange: ['inaudible', 'unintelligible', 'foreign', 'silence', 'crosstalk'] as const,
  paired: ['attempt', 'partial word'] as const,
  speaker: 'Speaker',
} as const

// Tags with canonical casing (how they should appear in output)
const CANONICAL_FORM: Record<string, string> = {
  // no timestamp
  'laughter': '[laughter]',
  'clapping': '[clapping]',
  'music': '[music]',
  'noise': '[noise]',
  'singing': '[singing]',
  'partial sentence': '[partial sentence]',

  // requires range (note: [Silence] keeps capital S per guideline examples)
  'inaudible': '[inaudible]',
  'unintelligible': '[unintelligible]',
  'foreign': '[foreign]',
  'silence': '[Silence]',
  'crosstalk': '[crosstalk]',

  // paired
  'attempt_open': '[attempt]',
  'attempt_close': '[/attempt]',
  'partial word_open': '[partial word]',
  'partial word_close': '[/partial word]',
}

// Utility to build issues
function issue(idCounter: () => string, severity: QAIssueSeverity, code: string, message: string, index: number, length: number, sample: string): QAIssue {
  return { id: idCounter(), severity, code, message, index, length, sample }
}

// Generate monotonically increasing IDs
function idFactory() {
  let n = 0
  return () => `qa_${++n}`
}

function clampRange(text: string, start: number, end: number) {
  const s = Math.max(0, Math.min(text.length, start))
  const e = Math.max(s, Math.min(text.length, end))
  return [s, e] as const
}

function snippet(text: string, start: number, length: number) {
  const [s, e] = clampRange(text, start, start + length)
  return text.slice(s, e)
}

function isWhitespace(ch: string | undefined) {
  return ch !== undefined && /\s/.test(ch)
}

function isPunctuation(ch: string | undefined) {
  return ch !== undefined && /[.,!?;:]/.test(ch)
}

function isLetterOrDigit(ch: string | undefined) {
  return ch !== undefined && /[A-Za-z0-9]/.test(ch)
}

function parseBracketSegments(text: string) {
  // Return all [ ... ] segments with their indices.
  // This does not handle nesting (not expected in this domain)
  const out: { content: string; start: number; end: number }[] = []
  let i = 0
  while (i < text.length) {
    const open = text.indexOf('[', i)
    if (open === -1) break
    const close = text.indexOf(']', open + 1)
    if (close === -1) {
      // no closing bracket for this '['
      out.push({ content: text.slice(open + 1), start: open, end: text.length })
      break
    }
    out.push({ content: text.slice(open + 1, close), start: open, end: close + 1 })
    i = close + 1
  }
  return out
}

function normalizeTagName(raw: string) {
  // Extract the raw "name" part and the potential payload (timestamps etc.)
  // Examples:
  // "laughter" => name="laughter", rest=""
  // "inaudible 00:..." => name="inaudible", rest="00:..."
  // "/attempt" => name="/attempt", rest=""
  // "crosstalk 00:..-.." => name="crosstalk", rest="00:..-.."
  // "foreign Italian 00:..-.." => name="foreign", rest="Italian 00:..-.."
  // "partial word" => name="partial word", rest=""
  // "partial sentence" => name="partial sentence", rest=""
  const trimmed = raw.trim()
  const sp = trimmed.split(/\s+/)
  if (sp.length === 0) return { name: '', rest: '' }

  const first = sp[0]

  // Closing tags like [/attempt], [/partial word] (note: we keep the leading '/')
  if (/^\/\w+/i.test(first)) {
    // e.g. first="/partial", rest may contain "word"
    return { name: first.toLowerCase(), rest: sp.slice(1).join(' ') }
  }

  // Speaker tags preserve capital S
  if (first.toLowerCase() === 'speaker') {
    return { name: 'Speaker', rest: sp.slice(1).join(' ') }
  }

  // Handle known multi-word tag names so that "partial word" and "partial sentence"
  // are treated as a single canonical name rather than name="partial", rest="word"
  if (sp.length >= 2) {
    const two = (sp[0] + ' ' + sp[1]).toLowerCase()
    if (two === 'partial word' || two === 'partial sentence') {
      return { name: two, rest: sp.slice(2).join(' ') }
    }
  }

  // Default: single token tag name with the remainder as payload
  return { name: first.toLowerCase(), rest: sp.slice(1).join(' ') }
}

function toTimestampParts(val: string) {
  const m = TIMESTAMP_RE.exec(val)
  if (!m) return null
  const [hh, mm, ss, ms] = [val.slice(0, 2), val.slice(3, 5), val.slice(6, 8), val.slice(9, 12)]
  const total = (+hh) * 3600 + (+mm) * 60 + (+ss) + (+ms) / 1000
  return { hh, mm, ss, ms, total }
}

function checkTimestampRange(range: string) {
  const m = RANGE_RE.exec(range)
  if (!m) return { ok: false, reason: 'Invalid range format' as const }
  const a = toTimestampParts(m[1])!
  const b = toTimestampParts(m[2])!
  if (a.total > b.total) return { ok: false, reason: 'Start timestamp is after end timestamp' as const }
  return { ok: true as const }
}

function indexToLineCol(text: string, index: number) {
  let line = 1
  let col = 1
  for (let i = 0; i < index && i < text.length; i++) {
    if (text[i] === '\n') { line++; col = 1 }
    else col++
  }
  return { line, col }
}

export function validateTranscript(text: string, meta: MetaLike): QAResult {
  const makeId = idFactory()
  const issues: QAIssue[] = []

  // 1) Bracket balance quick checks
  const opens = (text.match(/\[/g) || []).length
  const closes = (text.match(/\]/g) || []).length
  if (opens !== closes) {
    issues.push(issue(makeId, 'error', 'BRACKETS_UNBALANCED', `Mismatched square brackets: found ${opens} '[' and ${closes} ']'`, 0, Math.min(80, text.length), snippet(text, 0, 80)))
  }

  // 1.1) Leading/trailing whitespace
  {
    const leadMatch = text.match(/^\s+/)
    const lead = leadMatch ? leadMatch[0] : ''
    if (lead.length) {
      issues.push(issue(makeId, 'warning', 'LEADING_WHITESPACE', 'Leading whitespace detected at start of transcript', 0, lead.length, snippet(text, 0, Math.min(lead.length + 10, 40))))
    }
    const trailMatch = text.match(/\s+$/)
    const trail = trailMatch ? trailMatch[0] : ''
    if (trail.length) {
      const start = text.length - trail.length
      issues.push(issue(makeId, 'warning', 'TRAILING_WHITESPACE', 'Trailing whitespace detected at end of transcript', start, trail.length, snippet(text, Math.max(0, start - 10), Math.min(40, trail.length + 10))))
    }
  }

  // 2) Spacing around tags: word[ or ]word
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (ch === '[' && i > 0 && !isWhitespace(text[i - 1]) && text[i - 1] !== '[') {
      issues.push(issue(makeId, 'warning', 'SPACE_MISSING_BEFORE_TAG', 'Missing space before tag', i - 1, 2, snippet(text, Math.max(0, i - 10), 20)))
    }
    if (ch === ']' && i + 1 < text.length) {
      const next = text[i + 1]
      if (!isWhitespace(next) && next !== ']' && !isPunctuation(next)) {
        issues.push(issue(makeId, 'warning', 'SPACE_MISSING_AFTER_TAG', 'Missing space after tag', i, 2, snippet(text, Math.max(0, i - 10), 20)))
      }
    }
  }

  // 3) Parse bracket segments and validate tags
  const segments = parseBracketSegments(text)
  const speakerTagsInText = new Set<string>()
  // Track pairing for attempt/partial word and crosstalk (paired)
  const pairStack: { name: 'attempt' | 'partial word' | 'crosstalk'; index: number; ts?: string }[] = []

  segments.forEach(seg => {
    const raw = seg.content
    const segText = text.slice(seg.start, seg.end)
    const hasLeadingSpace = /^\s/.test(raw)
    const hasTrailingSpace = /\s$/.test(raw)
    if (hasLeadingSpace || hasTrailingSpace) {
      issues.push(issue(makeId, 'error', 'TAG_INNER_SPACE', 'Remove spaces immediately inside square brackets (e.g., use "[partial sentence]" not "[ partial sentence ]").', seg.start, seg.end - seg.start, segText))
    }
    const { name, rest } = normalizeTagName(raw)

    // Detect blatantly malformed tag content (e.g., whitespace-only)
    if (!name) {
      issues.push(issue(makeId, 'error', 'TAG_EMPTY', 'Empty tag detected "[]"', seg.start, seg.end - seg.start, segText))
      return
    }

    // Speaker tags
    if (name === CANONICAL_TAGS.speaker) {
      const restTrim = rest.trim()
      if (!/^\d+$/.test(restTrim)) {
        issues.push(issue(makeId, 'error', 'SPEAKER_FORMAT', 'Speaker tag must be a single number like [Speaker 1]', seg.start, seg.end - seg.start, segText))
      }
      // Canonical case: prefer [Speaker N] (capital S)
      if (!/^\[Speaker\s+\d+\]/.test(segText)) {
        const mCase = segText.match(/^\[speaker\s+\d+\]/)
        if (mCase) {
          issues.push(issue(makeId, 'warning', 'CANONICAL_CASE_SPEAKER', 'Prefer [Speaker N] with capital S.', seg.start, seg.end - seg.start, segText))
        }
      }
      const speakerNum = (restTrim.match(/^(\d+)$/) || [])[1]
      if (speakerNum) {
        speakerTagsInText.add(`Speaker ${speakerNum}`)
      }
      // Enforce space after closing bracket if followed by text
      const after = text[seg.end] || ''
      if (after && !isWhitespace(after) && !isPunctuation(after)) {
        issues.push(issue(makeId, 'warning', 'SPACE_AFTER_SPEAKER', 'Add a space after speaker tag', seg.end - 1, 2, segText))
      }
      return
    }

    // Closing tags
    if (name.startsWith('/')) {
      const closing = name.slice(1)
      if (closing === 'attempt' || closing === 'partial' || closing === 'partial word' || closing === 'crosstalk') {
        let normalized: 'attempt' | 'partial word' | 'crosstalk' | null = null
        if (closing === 'attempt') normalized = 'attempt'
        else if (closing === 'partial' || closing === 'partial word') normalized = 'partial word'
        else if (closing === 'crosstalk') normalized = 'crosstalk'
        if (normalized) {
          // For crosstalk paired variant, a timestamp is required in the closing tag too per guideline
          if (normalized === 'crosstalk') {
            if (!TIMESTAMP_RE.test(rest.trim())) {
              issues.push(issue(makeId, 'error', 'CROSSTALK_CLOSE_TIMESTAMP', '[/crosstalk] must include a timestamp (paired variant)', seg.start, seg.end - seg.start, segText))
            }
          }
          // Find last matching opening in stack
          for (let i = pairStack.length - 1; i >= 0; i--) {
            if (pairStack[i].name === normalized) {
              // Additional validation for crosstalk paired variant:
              if (normalized === 'crosstalk') {
                const closeTsRaw = rest.trim()
                if (TIMESTAMP_RE.test(closeTsRaw)) {
                  const openEntry = pairStack[i]
                  if (openEntry.ts) {
                    const openTs = toTimestampParts(openEntry.ts)
                    const closeTs = toTimestampParts(closeTsRaw)
                    if (openTs && closeTs && closeTs.total < openTs.total) {
                      issues.push(issue(makeId, 'error', 'CROSSTALK_CLOSE_BEFORE_OPEN', '[/crosstalk t] timestamp is before opening [crosstalk t] timestamp', seg.start, seg.end - seg.start, segText))
                    }
                  }
                }
              }
              pairStack.splice(i, 1)
              return
            }
          }
          issues.push(issue(makeId, 'error', 'PAIRING_MISMATCH', `Closing tag ${segText} does not have a matching opening tag`, seg.start, seg.end - seg.start, segText))
          return
        }
      }
      // Unknown closing tag
      issues.push(issue(makeId, 'warning', 'UNKNOWN_CLOSING_TAG', `Unknown closing tag ${segText}`, seg.start, seg.end - seg.start, segText))
      return
    }

    // Paired open tags
    if (name === 'attempt' || name === 'partial' || name === 'partial word') {
      const normalized: 'attempt' | 'partial word' = name === 'attempt' ? 'attempt' : 'partial word'
      // Must not carry timestamps
      if (rest.trim()) {
        issues.push(issue(makeId, 'warning', 'PAIRED_SHOULD_NOT_HAVE_TS', `Tag [${normalized}] should not include timestamps inside the opening tag`, seg.start, seg.end - seg.start, segText))
      }
      // Recommend space after opening tag within content (handled by spacing fixer)
      pairStack.push({ name: normalized, index: seg.start })
      return
    }

    // Crosstalk single-range or paired start
    if (name === 'crosstalk') {
      const r = rest.trim()
      if (!r) {
        issues.push(issue(makeId, 'error', 'CROSSTALK_NEEDS_TS', '[crosstalk] must either carry a start-end timestamp, or be used as paired [crosstalk t]...[/crosstalk t]', seg.start, seg.end - seg.start, segText))
      } else if (RANGE_RE.test(r)) {
        const ck = checkTimestampRange(r)
        if (!ck.ok) {
          issues.push(issue(makeId, 'error', 'TIMESTAMP_ORDER', `Timestamp range invalid: ${ck.reason}`, seg.start, seg.end - seg.start, segText))
        }
      } else if (TIMESTAMP_RE.test(r)) {
        // Paired variant requires closing tag later
        pairStack.push({ name: 'crosstalk', index: seg.start, ts: r })
      } else {
        issues.push(issue(makeId, 'error', 'CROSSTALK_TS_FORMAT', 'Invalid crosstalk timestamp format', seg.start, seg.end - seg.start, segText))
      }
      return
    }

  // Range-required tags
  if ((CANONICAL_TAGS.requiresRange as readonly string[]).includes(name)) {
    const r = rest.trim()
    if (name === 'foreign') {
      if (!r) {
        issues.push(issue(makeId, 'error', 'RANGE_MISSING', `Tag [foreign] must include a timestamp range`, seg.start, seg.end - seg.start, segText))
        return
      }
      // Accept optional language token before the range, e.g. "Italian 00:..-00:.."
      let rangePart = r
      const m = r.match(/^([A-Za-z]+)\s+(.+)$/)
      if (m) {
        rangePart = m[2]
      }
      if (!RANGE_RE.test(rangePart)) {
        issues.push(issue(makeId, 'error', 'RANGE_FORMAT', `Tag [foreign] must include a valid range (hh:mm:ss.sss-hh:mm:ss.sss)`, seg.start, seg.end - seg.start, segText))
      } else {
        const ck = checkTimestampRange(rangePart)
        if (!ck.ok) {
          issues.push(issue(makeId, 'error', 'TIMESTAMP_ORDER', `Timestamp range invalid: ${ck.reason}`, seg.start, seg.end - seg.start, segText))
        }
      }
      return
    } else {
      if (!r) {
        issues.push(issue(makeId, 'error', 'RANGE_MISSING', `Tag [${name}] must include a timestamp range`, seg.start, seg.end - seg.start, segText))
      } else if (!RANGE_RE.test(r)) {
        issues.push(issue(makeId, 'error', 'RANGE_FORMAT', `Tag [${name}] must include a valid range (hh:mm:ss.sss-hh:mm:ss.sss)`, seg.start, seg.end - seg.start, segText))
      } else {
        const ck = checkTimestampRange(r)
        if (!ck.ok) {
          issues.push(issue(makeId, 'error', 'TIMESTAMP_ORDER', `Timestamp range invalid: ${ck.reason}`, seg.start, seg.end - seg.start, segText))
        }
      }
      return
    }
  }

    // "foreign Language" special-casing: accept [foreign <Lang> start-end]
    if (name === 'foreign') {
      // Already processed in requiresRange
      return
    }

    // No-timestamp single tags
    if ((CANONICAL_TAGS.singleNoTs as readonly string[]).includes(name)) {
      if (ANY_RANGE_RE.test(rest) || TIMESTAMP_RE.test(rest)) {
        issues.push(issue(makeId, 'warning', 'SINGLE_SHOULD_NOT_HAVE_TS', `Tag [${name}] should not include timestamps`, seg.start, seg.end - seg.start, segText))
      }
      return
    }

    // Unknown tag name => suggest close form
    issues.push(issue(makeId, 'warning', 'UNKNOWN_TAG', `Unknown or non-canonical tag [${raw}]`, seg.start, seg.end - seg.start, segText))
  })

  // 3.x) Paired tags must contain non-whitespace content between open and close
  // Checks done with regex post-pass to avoid dependency on parsing order.
  {
    // attempt
    const reAttempt = /\[attempt\]([\s\S]*?)\[\/attempt\]/gi
    let m: RegExpExecArray | null
    while ((m = reAttempt.exec(text)) !== null) {
      const between = m[1]
      if (!/\S/.test(between)) {
        const start = m.index + '[attempt]'.length + 0
        const len = between.length
        issues.push(issue(makeId, 'warning', 'EMPTY_PAIRED_CONTENT', 'Empty content between [attempt] and [/attempt]', start, len, snippet(text, Math.max(0, start - 10), Math.min(40, len + 20))))
      }
    }
    // partial word
    const rePartial = /\[partial\s+word\]([\s\S]*?)\[\/partial\s+word\]/gi
    while ((m = rePartial.exec(text)) !== null) {
      const between = m[1]
      if (!/\S/.test(between)) {
        const openLen = m[0].indexOf(']') + 1 // end of opening bracket relative to whole match
        const start = m.index + openLen
        const len = between.length
        issues.push(issue(makeId, 'warning', 'EMPTY_PAIRED_CONTENT', 'Empty content between [partial word] and [/partial word]', start, len, snippet(text, Math.max(0, start - 10), Math.min(40, len + 20))))
      }
    }
    // crosstalk paired variant: [crosstalk t] ... [/crosstalk t]
    const ts = '\\\\d{2}:\\\\d{2}:\\\\d{2}\\\\.\\\\d{3}'
    const reCross = new RegExp(`\\[crosstalk\\s+(${ts})\\]([\\s\\S]*?)\\[/crosstalk\\s+(${ts})\\]`, 'gi')
    while ((m = reCross.exec(text)) !== null) {
      const between = m[2]
      if (!/\\S/.test(between)) {
        // start after the closing ']' of the opening tag
        const openTagEnd = m[0].indexOf(']') + 1
        const start = m.index + openTagEnd
        const len = between.length
        issues.push(issue(makeId, 'warning', 'EMPTY_PAIRED_CONTENT', 'Empty content between [crosstalk t] and [/crosstalk t]', start, len, snippet(text, Math.max(0, start - 10), Math.min(40, len + 20))))
      }
    }
  }

  // Unclosed pairs
  for (const p of pairStack) {
    issues.push(issue(makeId, 'error', 'UNCLOSED_PAIR', `Unclosed tag [${p.name}]`, p.index, 1, snippet(text, p.index, 20)))
  }

  // 4) Speaker presence check
  if (!/\[Speaker\s+\d+\]/.test(text)) {
    issues.push(issue(makeId, 'warning', 'SPEAKER_MISSING', 'No speaker tags found. Insert [Speaker N] at the beginning of each turn.', 0, Math.min(60, text.length), snippet(text, 0, 60)))
  }

  // 5) Percent and currency symbols
  if (/%/.test(text)) {
    issues.push(issue(makeId, 'warning', 'PERCENT_SYMBOL', 'Avoid using %; spell out and include numerals in curly braces per guideline.', Math.max(0, text.indexOf('%') - 5), 10, snippet(text, Math.max(0, text.indexOf('%') - 10), 20)))
  }
  if (/[€£$]\s*\d/.test(text)) {
    const m = text.match(/[€£$]\s*\d/)
    if (m) {
      const idx = text.indexOf(m[0])
      issues.push(issue(makeId, 'warning', 'CURRENCY_SYMBOL', 'Avoid currency symbols; spell out and include numerals in curly braces per guideline.', idx, m[0].length, snippet(text, Math.max(0, idx - 10), 20)))
    }
  }

  // 6) Digits not inside curly braces (heuristic; allow timestamps inside [ ... ])
  // We exclude [-ranges] content by temporarily removing bracket segments
  const textWithoutBrackets = text.replace(/\[[^\]]*\]/g, (m) => ' '.repeat(m.length))
  const digits = [...textWithoutBrackets.matchAll(/\b\d+(?:\.\d+)?\b/g)]
  for (const d of digits) {
    const start = d.index || 0
    const val = d[0]
    // If inside { } skip
    const before = textWithoutBrackets.lastIndexOf('{', start)
    const after = textWithoutBrackets.indexOf('}', start + val.length)
    const insideBraces = before !== -1 && after !== -1 && before < start && after > start
    if (insideBraces) continue
    issues.push(issue(makeId, 'info', 'NUMBER_NO_BRACES', `Number "${val}" not inside curly braces { } (check guideline exceptions like times or names).`, start, val.length, snippet(text, Math.max(0, start - 10), 20)))
  }

  // 7) Curly braces containing colon like times, which should not use braces
  const curlyTime = [...text.matchAll(/\{[^}]*:\d{2}[^}]*\}/g)]
  for (const m of curlyTime) {
    const idx = m.index || 0
    issues.push(issue(makeId, 'warning', 'TIME_IN_BRACES', 'Avoid using curly braces for times (e.g., "{1:00}") per guideline.', idx, m[0].length, snippet(text, Math.max(0, idx - 10), 20)))
  }

  // 7.0.1) Braced numbers should be accompanied by spelled-out words nearby (heuristic)
  const bracedNumbers = [...text.matchAll(/\{[0-9][^}]*\}/g)]
  for (const m of bracedNumbers) {
    const idx = m.index || 0
    // Look back up to 20 chars for a preceding word token
    const back = text.slice(Math.max(0, idx - 20), idx)
    const prevWord = back.match(/([A-Za-z][A-Za-z-]*)\s*$/)
    if (!prevWord) {
      issues.push(issue(makeId, 'info', 'BRACED_NUMBER_NO_SPELLED', 'Numeric value in { } should be accompanied by the spelled-out number in text.', idx, m[0].length, snippet(text, Math.max(0, idx - 10), 30)))
    }
  }

  // 7.1) Emails and URLs should be written out (e.g., "name at domain dot com", "example dot com")
  const emailLike = [...text.matchAll(/\b[\w.+-]+@[\w-]+\.[\w.-]+\b/g)]
  for (const m of emailLike) {
    const idx = m.index || 0
    issues.push(issue(makeId, 'warning', 'EMAIL_SYMBOL', 'Emails should be written out (e.g., "name at domain dot com").', idx, m[0].length, snippet(text, Math.max(0, idx - 10), 30)))
  }
  const urlLike = [...text.matchAll(/\b(?:https?:\/\/|www\.)[^\s]+/gi)]
  for (const m of urlLike) {
    const idx = m.index || 0
    issues.push(issue(makeId, 'warning', 'URL_SYMBOL', 'Websites should be written out (e.g., "example dot com").', idx, m[0].length, snippet(text, Math.max(0, idx - 10), 30)))
  }

  // 7.2) Units abbreviations discouraged (spell out words, use numerals in { } when applicable)
  const units = [...text.matchAll(/\b\d+[ ,.]?\d*\s?(?:lbs?|#|km|kg|g|mg|cm|mm|m|km\/h|mph)\b/gi)]
  for (const m of units) {
    const idx = m.index || 0
    issues.push(issue(makeId, 'warning', 'UNIT_ABBREVIATION', 'Avoid unit abbreviations (e.g., "lbs", "km"); spell out words and include numerals in { } as per guideline.', idx, m[0].length, snippet(text, Math.max(0, idx - 10), 30)))
  }

  // 7.3) Laughter should be annotated, not transcribed as "hahaha"
  const laughterText = [...text.matchAll(/\b(?:ha){3,}\b/gi)]
  for (const m of laughterText) {
    const idx = m.index || 0
    issues.push(issue(makeId, 'warning', 'LAUGHTER_TEXT', 'Do not transcribe laughter as "hahaha"; use [laughter].', idx, m[0].length, snippet(text, Math.max(0, idx - 10), 30)))
  }

  // 7.4) [foreign Language ...] placeholder should be replaced with the identified language or use generic [foreign ...]
  const foreignPlaceholder = [...text.matchAll(/\[foreign\s+Language\b[^\]]*\]/gi)]
  for (const m of foreignPlaceholder) {
    const idx = m.index || 0
    issues.push(issue(makeId, 'warning', 'FOREIGN_PLACEHOLDER', 'Replace "Language" placeholder in [foreign Language ...] with the identified language, or use [foreign ...] if unknown.', idx, m[0].length, snippet(text, Math.max(0, idx - 10), 30)))
  }

  // 7.5) Times like "1:00" (outside brackets) should be spelled out (heuristic)
  const timesDigits = [...text.replace(/\[[^\]]*\]/g, (m) => ' '.repeat(m.length)).matchAll(/\b\d{1,2}:\d{2}\b/g)]
  for (const m of timesDigits) {
    const idx = m.index || 0
    issues.push(issue(makeId, 'info', 'TIME_DIGITS', 'Times should generally be spelled out (e.g., "one o’clock").', idx, m[0].length, snippet(text, Math.max(0, idx - 10), 20)))
  }

  // 7.6) Place name abbreviations (e.g., Ft., Mt.) discouraged
  const abbrevPlaces = [...text.matchAll(/\b(Ft\.|Mt\.)\b/g)]
  for (const m of abbrevPlaces) {
    const idx = m.index || 0
    issues.push(issue(makeId, 'info', 'PLACE_ABBREV', 'Avoid abbreviations in official place names (e.g., "Ft." → "Fort", "Mt." → "Mount").', idx, m[0].length, snippet(text, Math.max(0, idx - 10), 20)))
  }

  // 7.7) Silence-only transcript cross-checks
  const onlySilence = /^\s*\[Silence\s+\d{2}:\d{2}:\d{2}\.\d{3}-\d{2}:\d{2}:\d{2}\.\d{3}\]\s*$/i.test(text.trim())
  if (onlySilence) {
    if (meta.language && meta.language !== 'Unidentified') {
      issues.push(issue(makeId, 'info', 'SILENCE_LANGUAGE', 'All-silence transcript: consider setting language to "Unidentified".', 0, 0, ''))
    }
  }

  // 7.8) Language is "Unidentified" but content appears present
  {
    const textNoBrackets = text.replace(/\[[^\]]*\]/g, (m) => ' '.repeat(m.length))
    const hasAlpha = /[A-Za-z]{3,}/.test(textNoBrackets)
    if ((meta.language || '').trim() === 'Unidentified' && hasAlpha) {
      issues.push(issue(makeId, 'info', 'LANGUAGE_UNIDENTIFIED_WITH_CONTENT', 'Language marked as "Unidentified" but transcript appears to contain words. Verify language.', 0, 0, ''))
    }
  }

  // 8) Metadata cross-checks
  // Language present
  if (!meta || !meta.language || !meta.language.trim()) {
    issues.push(issue(makeId, 'warning', 'LANGUAGE_EMPTY', 'Metadata "language" is empty. Use the appropriate language or "Unidentified".', 0, 0, ''))
  }
  // Meeting present (the UI always fills it; still validate type)
  if (typeof meta?.meeting !== 'boolean') {
    issues.push(issue(makeId, 'warning', 'MEETING_MISSING', 'Metadata "meeting" missing (true/false).', 0, 0, ''))
  }
  // Speakers cross-reference
  const speakerRegexNames = Array.from(new Set([...(text.matchAll(/\[speaker\s+(\d+)\]/gi))].map(m => `Speaker ${m[1]}`)))
  const speakerTagNames = Array.from(new Set([...speakerRegexNames, ...speakerTagsInText]))
  const metaNames = (meta?.speakers || []).map(s => s.name || '').filter(Boolean)
  const metaByName = new Map<string, { name: string; gender?: string; dialect?: string }>()
  for (const sp of meta?.speakers || []) {
    if (sp?.name) metaByName.set(sp.name, sp)
  }
  for (const t of speakerTagNames) {
    if (!metaNames.includes(t)) {
      issues.push(issue(makeId, 'error', 'SPEAKER_NOT_IN_META', `Speaker tag "${t}" not present in metadata speakers list.`, 0, 0, ''))
    }
  }

  // 8.1) Suggest adding gender/dialect where feasible (informational) ONLY for speakers actually used in transcript
  for (const name of speakerTagNames) {
    const sp = metaByName.get(name)
    if (!sp) continue
    if (!sp.gender) {
      issues.push(issue(makeId, 'info', 'SPEAKER_GENDER_MISSING', `Consider adding gender for "${sp.name}" when identifiable ("Male", "Female", or "Unidentified").`, 0, 0, ''))
    }
    if (!sp.dialect) {
      issues.push(issue(makeId, 'info', 'SPEAKER_DIALECT_MISSING', `Consider adding dialect/region for "${sp.name}" when identifiable.`, 0, 0, ''))
    }
  }

  // 8.2) Foreign-language tag vs metadata language consistency
  // The foreign tag indicates segments in a different language than the primary metadata language.
  // Do not flag when they differ (that is expected). Only flag if they are the same language,
  // which would make the "foreign" tag questionable.
  {
    const foreignLangs = new Set<string>()
    for (const seg of segments) {
      const { name, rest } = normalizeTagName(seg.content)
      if (name === 'foreign') {
        const langToken = (rest.trim().match(/^([A-Za-z]+)/) || [])[1]
        if (langToken && langToken.toLowerCase() !== 'language') {
          foreignLangs.add(langToken)
        }
      }
    }
    const metaLang = (meta.language || '').trim()
    for (const fl of foreignLangs) {
      if (metaLang === 'Unidentified') {
        issues.push(issue(makeId, 'info', 'LANGUAGE_DETECTABLE_FROM_TAGS', `Language appears identifiable from tags ("${fl}"). Consider setting metadata language accordingly.`, 0, 0, ''))
      } else if (metaLang && metaLang.toLowerCase() === fl.toLowerCase()) {
        issues.push(issue(makeId, 'info', 'FOREIGN_EQUALS_METADATA', `Foreign-language tag "${fl}" matches the metadata language "${metaLang}". Verify that this segment is truly foreign.`, 0, 0, ''))
      }
    }
  }

  // 9) Canonical casing suggestions
  // Silence should be [Silence]; others mostly lowercase
  for (const seg of segments) {
    const raw = seg.content
    const { name } = normalizeTagName(raw)
    const segText = text.slice(seg.start, seg.end)
    if (name === 'silence' && segText !== segText.replace(/\[silence/i, '[Silence')) {
      issues.push(issue(makeId, 'info', 'CANONICAL_CASE_SILENCE', 'Prefer [Silence] with capital S.', seg.start, seg.end - seg.start, segText))
    }
    if (name === 'laughter' && /\[Laughter\]/.test(segText)) {
      issues.push(issue(makeId, 'info', 'CANONICAL_CASE_LAUGHTER', 'Prefer [laughter] in lowercase.', seg.start, seg.end - seg.start, segText))
    }
  }

  const counts = {
    error: issues.filter(i => i.severity === 'error').length,
    warning: issues.filter(i => i.severity === 'warning').length,
    info: issues.filter(i => i.severity === 'info').length,
    total: issues.length,
  }
  return { issues, counts }
}

// Auto-fixers (safe only)
// - Add missing spaces around tags (before '[' when needed and after ']' when needed)
// - Ensure space after [attempt] and [partial word] openings and before closing [/attempt], [/partial word]
// - Normalize canonical casing for simple tags
export function autoFixTranscript(text: string) {
  let changed = false
  // 0) Trim leading/trailing whitespace
  {
    const t = text.replace(/^\s+/, '').replace(/\s+$/, '')
    if (t !== text) { text = t; changed = true }
  }
  // 1) Spaces around tags
  {
    let buf = ''
    for (let i = 0; i < text.length; i++) {
      const ch = text[i]
      if (ch === '[') {
        const prev = text[i - 1]
        if (i > 0 && !isWhitespace(prev) && prev !== '[') {
          buf += ' '
          changed = true
        }
        buf += ch
      } else if (ch === ']') {
        buf += ch
        const next = text[i + 1]
        if (next && !isWhitespace(next) && next !== ']' && !isPunctuation(next)) {
          buf += ' '
          changed = true
        }
      } else {
        buf += ch
      }
    }
    text = buf
  }

  // 2) Spaces inside paired tags
  // After [attempt] and [partial word]
  text = text.replace(/\[attempt\](\S)/gi, (_m, g1) => {
    changed = true
    return '[attempt] ' + g1
  })
  text = text.replace(/\[partial\s+word\](\S)/gi, (_m, g1) => {
    changed = true
    return '[partial word] ' + g1
  })
  // Before closing tags
  text = text.replace(/(\S)\[\/attempt\]/gi, (_m, g1) => {
    changed = true
    return g1 + ' [/attempt]'
  })
  text = text.replace(/(\S)\[\/partial\s+word\]/gi, (_m, g1) => {
    changed = true
    return g1 + ' [/partial word]'
  })

  // 3) Canonical casing simple tags
  const replacements: Array<[RegExp, string]> = [
    [/\[silence\]/gi, '[Silence]'],
    [/\[laughter\]/gi, '[laughter]'],
    [/\[clapping\]/gi, '[clapping]'],
    [/\[music\]/gi, '[music]'],
    [/\[noise\]/gi, '[noise]'],
    [/\[singing\]/gi, '[singing]'],
    [/\[partial\s+sentence\]/gi, '[partial sentence]'],
    [/\[attempt\]/gi, '[attempt]'],
    [/\[\/attempt\]/gi, '[/attempt]'],
    [/\[partial\s+word\]/gi, '[partial word]'],
    [/\[\/partial\s+word\]/gi, '[/partial word]'],
    [/\[crosstalk\]/gi, '[crosstalk]'],
    [/\[inaudible\]/gi, '[inaudible]'],
    [/\[unintelligible\]/gi, '[unintelligible]'],
    [/\[foreign\]/gi, '[foreign]'],
  ]
  for (const [re, to] of replacements) {
    const before = text
    text = text.replace(re, (m) => {
      if (m === to) return m
      return to
    })
    if (text !== before) changed = true
  }

  // 4) Normalize [speaker N] -> [Speaker N]
  {
    const before = text
    text = text.replace(/\[speaker\s+(\d+)\]/gi, (_m, g1) => `[Speaker ${g1}]`)
    if (text !== before) changed = true
  }

  // 5) Add missing closing tags for simple pairs (safe heuristic)
  {
    const openAttempts = (text.match(/\[attempt\]/gi) || []).length
    const closeAttempts = (text.match(/\[\/attempt\]/gi) || []).length
    if (openAttempts > closeAttempts) {
      text = text + ' ' + '[/attempt]'.repeat(openAttempts - closeAttempts)
      changed = true
    }

    const openPartial = (text.match(/\[partial\s+word\]/gi) || []).length
    const closePartial = (text.match(/\[\/partial\s+word\]/gi) || []).length
    if (openPartial > closePartial) {
      text = text + ' ' + '[/partial word]'.repeat(openPartial - closePartial)
      changed = true
    }

    // Close [crosstalk t] with missing [/crosstalk t]
    const opens: Array<{ ts: string }> = []
    const reOpen = /\[crosstalk\s+(\d{2}:\d{2}:\d{2}\.\d{3})\]/gi
    let m: RegExpExecArray | null
    while ((m = reOpen.exec(text)) !== null) {
      opens.push({ ts: m[1] })
    }
    for (const o of opens) {
      const closeRe = new RegExp(`\\[/crosstalk\\s+${o.ts.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}\\]`, 'i')
      if (!closeRe.test(text)) {
        text = text + ` [/crosstalk ${o.ts}]`
        changed = true
      }
    }
  }

  return { text, changed }
}
